<template>
  <section class="w-full max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5">
    <div class="space-y-6">
      <!-- SEO-optimized eyebrow -->
      <p
        class="text-xs uppercase tracking-wide text-emerald-600 dark:text-emerald-400 font-semibold"
      >
        ITIKAF 2026 - PAKISTAN
      </p>

      <!-- Hero section with cards -->
      <div class="space-y-4">
        <!-- H1 - SEO optimized with full semantic coverage -->
        <h1
          class="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 dark:text-gray-100 leading-tight"
        >
          {{ heroTitle }}
        </h1>

        <!-- Subtitle with context - clean Roman Urdu -->
        <p class="text-base sm:text-lg text-gray-600 dark:text-gray-400">
          {{ heroSubtitle }}
        </p>
      </div>

      <!-- Start date card - visual emphasis -->
      <div
        class="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/50 dark:to-teal-950/50 rounded-2xl p-6 border-2 border-emerald-200 dark:border-emerald-800 shadow-sm"
      >
        <p
          class="text-sm font-bold text-emerald-700 dark:text-emerald-400 mb-3 uppercase tracking-wide"
        >
          {{ heroCardLabel }}
        </p>
        <p
          class="text-4xl sm:text-5xl font-black text-gray-900 dark:text-gray-100 mb-2"
        >
          {{ heroCardDate }}
        </p>
        <p
          class="text-xl sm:text-2xl font-bold text-gray-700 dark:text-gray-300 mb-4"
        >
          {{ heroCardSubtext }}
        </p>
        <div
          class="flex items-center gap-2 text-sm sm:text-base text-gray-600 dark:text-gray-400 bg-white/60 dark:bg-gray-800/60 rounded-lg px-3 py-2 w-fit"
        >
          <svg
            class="w-5 h-5 text-emerald-600 dark:text-emerald-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span class="font-medium"
            >{{ heroCardHint }}</span
          >
        </div>
      </div>

      <!-- End date section - neutral gray tone -->
      <div class="space-y-2">
        <p
          class="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100"
        >
          {{ endSectionLabel }}
        </p>
        <div
          class="bg-gradient-to-r from-gray-100 to-slate-100 dark:from-gray-800/50 dark:to-slate-800/50 rounded-xl px-5 py-4 border border-gray-300 dark:border-gray-700 inline-flex items-center gap-3"
        >
          <svg
            class="w-6 h-6 text-gray-600 dark:text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
          <div>
            <p
              class="text-2xl sm:text-3xl font-black text-gray-900 dark:text-gray-100"
            >
              {{ endCardTitle }}
            </p>
            <p
              class="text-base sm:text-lg font-semibold text-gray-600 dark:text-gray-400"
            >
              {{ endCardSubtitle }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      class="inline-flex items-center gap-2 rounded-full bg-gray-100 dark:bg-gray-800/70 px-3 py-1 text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-100"
    >
      <span aria-hidden="true">&#x23F3;</span>
      <span>{{ countdownLabel }}</span>
    </div>

    <section id="dates" class="space-y-3">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Itikaf Kab Hota Hai?
      </h2>
      <ul
        class="list-disc pl-5 text-sm text-gray-700 dark:text-gray-300 space-y-1"
      >
        <li>20 Ramadan ki shaam se</li>
        <li>Eid ka chand tak</li>
        <li>Total: 10 din</li>
      </ul>
      <p class="text-sm font-semibold text-amber-700 dark:text-amber-400">
        <span aria-hidden="true">&#x26A0;</span>
        {{ statusNotice }}
      </p>
      <p class="text-xs text-gray-500 dark:text-gray-400">
        Note: Chand dekhne par exact tareekh confirm hogi.
      </p>
    </section>

    <section class="space-y-2">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Itikaf 2026 Pakistan – Mukammal Tafseel
      </h2>
      <p class="text-sm text-gray-700 dark:text-gray-300">
        {{ detailParagraphOne }}
      </p>
      <p class="text-sm text-gray-700 dark:text-gray-300">
        {{ detailParagraphTwo }}
      </p>
    </section>

    <SectionChips />

    <ExpandableSection title="Itikaf Ki Shartain" id="conditions">
      <ul class="list-disc pl-5 space-y-1">
        <li v-for="item in page?.rules?.conditions" :key="item">
          {{ item }}
        </li>
      </ul>
      <p class="mt-3">Kya jaiz hai:</p>
      <ul class="list-disc pl-5 mt-2 space-y-1">
        <li v-for="item in page?.rules?.permitted" :key="item">
          {{ item }}
        </li>
      </ul>
      <p class="mt-3">Kya mana hai:</p>
      <ul class="list-disc pl-5 mt-2 space-y-1">
        <li v-for="item in page?.rules?.prohibited" :key="item">
          {{ item }}
        </li>
      </ul>
      <p class="mt-3">{{ page?.rules?.breaking }}</p>
    </ExpandableSection>

    <ExpandableSection title="Itikaf Ki Niyat" id="niyat">
      <p>{{ page?.niyat?.arabic }}</p>
      <p class="mt-2 italic">{{ page?.niyat?.roman }}</p>
      <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {{ page?.niyat?.urdu }}
      </p>
      <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {{ page?.niyat?.note }}
      </p>
    </ExpandableSection>

    <ExpandableSection title="Aurton Ke Liye Itikaf" id="women">
      <p>{{ page?.womenSection?.description }}</p>
      <ul class="list-disc pl-5 mt-2 space-y-1">
        <li v-for="item in page?.womenSection?.conditions" :key="item">
          {{ item }}
        </li>
      </ul>
      <p class="mt-2">{{ page?.womenSection?.note }}</p>
    </ExpandableSection>

    <ExpandableSection title="Itikaf Registration" id="registration">
      <p>{{ page?.registration?.description }}</p>
      <ul class="list-disc pl-5 mt-2 space-y-1">
        <li v-for="item in page?.registration?.tips" :key="item">
          {{ item }}
        </li>
      </ul>
    </ExpandableSection>

    <ExpandableSection title="Itikaf Essentials" id="essentials">
      <ul class="list-disc pl-5 space-y-1">
        <li v-for="item in page?.essentials?.list" :key="item">
          {{ item }}
        </li>
      </ul>
    </ExpandableSection>

    <ExpandableSection title="Itikaf Schedule (Tajweez)" id="schedule">
      <ul class="list-disc pl-5 space-y-1">
        <li v-for="item in page?.schedule?.activities" :key="item">
          {{ item }}
        </li>
      </ul>
      <p class="mt-2 text-xs text-gray-500 dark:text-gray-400">
        {{ page?.schedule?.note }}
      </p>
    </ExpandableSection>

    <ExpandableSection title="Itikaf Ki Ahmiyat">
      <p>{{ page?.content?.intro }}</p>
      <p class="mt-2">{{ page?.content?.significance }}</p>
      <p class="mt-2">{{ page?.content?.pakistanTradition }}</p>
      <p class="mt-2">{{ page?.content?.message }}</p>
    </ExpandableSection>

    <p class="text-sm text-gray-700 dark:text-gray-300">
      Pakistan mein badi masjidon mein itikaf ka intezam hota hai, jagah limited
      hoti hai is liye registration jaldi karen.
    </p>

    <section id="faq" class="space-y-3">
      <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Itikaf 2026 Pakistan FAQ
      </h2>

      <ExpandableSection title="Itikaf 2026 kab shuru hoga?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf 2026 Ramzan ke 20vi tareekh ki shaam (Maghrib ke baad) shuru
          hoga. Pakistan mein umeedi tareekh 9 March 2026 (20 Ramadan 1447 AH)
          ki shaam hai. Itikaf Eid ka chand nazar aane tak rahega.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf ki niyat kaise karein?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf ki niyat dil se karni hoti hai. Albatta ye alfaz zaban se parh
          sakte hain: “Nawaytu an a'takifa fi haza al-masjidi ma dumtu feeh”
          (Main ne niyat ki is masjid mein itikaf ki jab tak mein yahan rahoon).
          Niyat karte waqt dil mein ye irada hona chahiye ke main Allah ki
          ibadat ke liye masjid mein theharta hoon.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf kis tarah ka amal hai?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf Ramzan ke aakhri 10 dinon mein masjid mein thehar kar ibadat
          karna hai. Ye Sunnat-e-Muakkadah hai. Itikaf ka maqsad dunya se alag
          ho kar Allah ki ibadat mein mashgool rehna aur Shab-e-Qadr ki talash
          karna hai.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf ke liye registration kaise karayein?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Pakistan ki badi masjidon mein Itikaf ke liye registration Ramzan se
          1â€“2 mahine pehle shuru ho jati hai. Data Darbar Lahore, Badshahi
          Masjid, Faisal Masjid Islamabad aur Minhaj-ul-Quran ki Itikaf City
          mein online aur offline registration hoti hai. Jaga limited hoti hai,
          is liye jald registration karana zaroori hai. Dawat-e-Islami aur dusre
          deeni tanzim bhi registration ka intezam karti hain.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Aurton ke liye Itikaf ka kya hukm hai?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Auratain bhi Itikaf kar sakti hain. Unke liye masjid mein alahida jaga
          ya ghar mein ibadat ki jaga muqarrar ki jati hai. Aurton ke liye shart
          ye hai ke: (1) Shauhar ya wali ki ijazat ho, (2) Ghar mein itikaf kar
          rahi hon to apni namaz ki jaga se be-wajah na niklein, (3) Mahwari ke
          dinon mein itikaf toot jata hai. Pakistan mein hazaron auratain har
          saal ghar mein ya masjidon ke khas hisson mein Itikaf karti hain.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf mein kya kya karna chahiye?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf ke doraan ye a'amal karna chahiye: (1) Quran ki tilawat, (2)
          Tahajjud ki namaz, (3) Nafl namazain, (4) Zikr-o-Azkaar, (5) Dua aur
          istighfar, (6) Shab-e-Qadr ki talash, (7) Deeni kitabon ka mutala, (8)
          Ghor-o-fikar. Itikaf mein Quran, namaz aur
          <NuxtLink to="/dua/" class="underline underline-offset-2">
            masnoon duain
          </NuxtLink>
          par khas tawajju deni chahiye. Itikaf ka har lamha ibadat mein guzarna
          chahiye.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf mein kya cheezein mana hain?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf mein ye kaam mana hain: (1) Masjid se be-wajah nikalna, (2)
          Dunyawi karobar (khareed o farokht) karna, (3) Shadi biya ki batain
          karna, (4) Apni biwi se jismani talluq qayam karna, (5) Faaltu aur
          behuda baton mein waqt zaya karna. Agar koi jan-bujh kar ye kaam kare
          to Itikaf toot jata hai.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf mein kya cheezein jaiz hain?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf mein ye kaam jaiz hain: (1) Masjid mein khana peena aur sona,
          (2) Wazu, ghusl aur bathroom ke liye masjid se nikalna, (3) Beemari ki
          halat mein ilaaj ke liye nikalna, (4) Kisi se baat cheet karna (lekin
          dunyawi baton se bachein), (5) Faisla-kun zaroorat (jaise aag lagna ya
          jan ka khatra) ke liye nikalna.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf ke liye sath kya le kar jayein?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf ke liye ye cheezein sath le jayen: (1) Quran shareef aur
          tafseer, (2) Duain aur wazaif ki kitab, (3) Deeni kitabein (Bahishti
          Zewar, Fazail-e-Amaal waghera), (4) Janamaz, (5) Safai ka saman
          (brush, miswak, soap), (6) Zaroori kapray (aam taur par 3â€“4 set),
          (7) Masjid mein istimal ke liye jootay, (8) Notebook aur pen (deeni
          batain likhne ke liye). Mobile phone, TV aur dunyawi cheezon se door
          rahein.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf mein Shab-e-Qadr kaise talash karein?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Ramzan ki aakhri 10 raaton mein khas taur par taaq raaton (21, 23, 25,
          27, 29 Ramzan) mein Shab-e-Qadr talash karein. Aksar Pakistan mein 27
          Ramzan ki raat ko Shab-e-Qadr mani jati hai. In raaton mein zyada se
          zyada nawafil parhein, Quran ki tilawat karein, aur khas dua
          “Allahumma innaka afuwwun tuhibbul afwa fa'fu anni” ka wazifa karein.
        </p>
      </ExpandableSection>

      <ExpandableSection
        title="Data Darbar Lahore mein Itikaf 2026 ke liye kitne log register hain?"
      >
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Data Darbar Lahore mein 2025 mein taqreeban 1,800 logon ne Itikaf kiya
          tha. 2026 mein bhi isi tarah ke intezarat hain. Punjab Auqaf
          Department list jari karta hai aur Special Branch clearance ke baad
          names final kiye jate hain. Wahan 4 sectors hain jismein 102 cabins
          hote hain.
        </p>
      </ExpandableSection>

      <ExpandableSection
        title="Faisal Masjid Islamabad mein Itikaf ki kya gunjaish hai?"
      >
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Faisal Masjid Islamabad mein 2025 mein taqreeban 600â€“1,000 logon ne
          Itikaf kiya tha. Yahan Itikaf ke liye koi paisa nahi liya jata.
          Registration ke liye masjid administration se rabta karna hota hai.
          Special Branch clearance ke baad CNICs par naam aur photo jari kiye
          jate hain.
        </p>
      </ExpandableSection>

      <ExpandableSection
        title="Itikaf City (Minhaj-ul-Quran) kya hai aur wahan registration kaise hoti hai?"
      >
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf City Minhaj-ul-Quran International ka ahtemam hai jo Lahore
          Township mein qayam hota hai. 2025 mein 8,500 mardon aur 3,500 aurton
          ne yahan Itikaf kiya. Yahan Shaykh-ul-Islam Dr Muhammad Tahir-ul-Qadri
          Taraweeh ke baad dars dete hain. Registration online hoti hai aur
          pehle se complete kar leni hoti hai. Security ke liye CCTV cameras aur
          walk-through gates hote hain, aur 24/7 medical facilities maujood hoti
          hain.
        </p>
      </ExpandableSection>

      <ExpandableSection
        title="Itikaf ke doraan khane ka intezam kaise hota hai?"
      >
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Pakistan ki masjidon mein Itikaf ke doraan Sehri aur Iftar ka intezam
          philanthropists (mukhlis log) karte hain. Badi masjidon mein khas taur
          par chefs rakhay jate hain aur Punjab Food Authority teams quality
          check karti hain. Itikaf City Lahore mein khas taur par hygienic meals
          ka intezam hota hai.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf ke doraan security ka kya intezam hai?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Pakistan ki badi masjidon mein Itikaf ke doraan security ke sakht
          intezarat hote hain: (1) Computerized ID cards (CNICs) jari kiye jate
          hain jin par naam aur photo hoti hai, (2) Entry par CCTV cameras
          lagaye jate hain, (3) Control room qayam kiya jata hai, (4)
          Walk-through gates installation, (5) Special Branch clearance. Data
          Darbar, Faisal Masjid aur Itikaf City mein security ka khas intezam
          hota hai.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf kab toot jata hai?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf in suraton mein toot jata hai: (1) Masjid se be-wajah nikalna
          (agar bhool kar nikal jayen to yaad aate hi wapas aa jayen, Itikaf
          nahi toota), (2) Dunyawi karobar karna, (3) Apni biwi se jismani
          talluq, (4) Be-wajah bahar raat guzar dena, (5) Deen chhor dene ka
          irada kar lena. Agar Itikaf toot jaye to qaza karna wajib hota hai.
        </p>
      </ExpandableSection>

      <ExpandableSection
        title="Kya Itikaf sirf masjid mein hota hai ya ghar mein bhi ho sakta hai?"
      >
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Mardon ke liye Itikaf sirf us masjid mein hota hai jahan namaz-e-jamat
          hoti ho. Aurton ke liye ghar mein ibadat ki jaga (tahajjuma) par
          Itikaf ho sakta hai. Aurton ki surat mein unki namaz ki jaga masjid ka
          darja nahi rakhti lekin Itikaf ho jata hai.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf mein Taraweeh ki namaz ka kya hukm hai?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf mein Taraweeh ki namaz jamat ke saath masjid mein parhna
          chahiye. Itikaf ka faidah yahi hai ke ibadat mein mashgool raha jaye.
          Agar kisi wajah se masjid mein Taraweeh na ho sake to dusri masjid ja
          sakte hain, lekin Itikaf ki jaga wohi masjid hogi jahan niyat ki thi.
          Dusri masjid sirf namaz ke liye jana jaiz hai, der tak theharna nahi.
        </p>
      </ExpandableSection>

      <ExpandableSection title="Itikaf ki kya fazilat hai?">
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Itikaf ki bahut badi fazilat hai: (1) Ye Sunnat-e-Muakkadah hai aur
          Nabi (SAW) ne is ka hamesha ahtemam kiya, (2) Itikaf mein insan
          Shab-e-Qadr ki talash karta hai jo hazar mahino se behtar hai, (3)
          Masjid mein rehkar gunahon se bachne ki mashq hoti hai, (4) Allah se
          qareebi ka zariya hai, (5) Dil ki safai aur tazkiya ka mauka hai.
        </p>
      </ExpandableSection>

      <ExpandableSection
        title="Itikaf ke liye kis umar ke log register kar sakte hain?"
      >
        <p class="text-sm text-gray-700 dark:text-gray-300">
          Pakistan ki masjidon mein aam taur par 14+ ya 16+ sal ke mardon ke
          liye Itikaf ki ijazat hoti hai. Data Darbar Lahore aur dusri badi
          masjidon mein 14+ ya 18+ sal ki umar muqarrar hai. Aurton ke liye aam
          taur par 12+ sal ki umar hoti hai. Kuch masjidon mein walidain ki
          ijazat se kam umar bachchon ko bhi ijazat mil jati hai.
        </p>
      </ExpandableSection>
    </section>

    <section class="space-y-2">
      <h2 class="text-sm font-semibold text-gray-900 dark:text-gray-100">
        Related Ramadan Guides
      </h2>
      <ul class="text-sm text-gray-700 dark:text-gray-300 space-y-1">
        <li>
          <NuxtLink to="/shab-e-qadr/" class="underline underline-offset-2">
            Shab-e-Qadr 2026 Pakistan
          </NuxtLink>
        </li>
        <li>
          <NuxtLink to="/calendar/" class="underline underline-offset-2">
            Ramadan Calendar 2026
          </NuxtLink>
        </li>
        <li>
          <NuxtLink to="/dua/" class="underline underline-offset-2">
            Ramadan Duain Collection
          </NuxtLink>
        </li>
      </ul>
    </section>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import SectionChips from "@/components/itikaf/SectionChips.vue";
import ExpandableSection from "@/components/itikaf/ExpandableSection.vue";

const { messages, locale } = useI18n();
const page = computed(() => messages.value[locale.value]?.pages?.itikaf2026);

const PAKISTAN_TIME_ZONE = "Asia/Karachi";

function getTodayInPakistan() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PAKISTAN_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

const todayInPakistan = computed(() => getTodayInPakistan());
const isChandRaatState = computed(() => todayInPakistan.value === "2026-03-20");
const isEidState = computed(() => todayInPakistan.value === "2026-03-21");

const heroTitle = computed(() => {
  if (isChandRaatState.value) return "Itikaf 2026 Complete - Aaj Chand Raat Hai, Kal Eid Hai";
  if (isEidState.value) return "Itikaf 2026 Complete - Aaj Eid ul-Fitr Hai";
  return "Itikaf 2026 Pakistan - 20 Ramadan se Eid ke Chand Tak";
});

const heroSubtitle = computed(() => {
  if (isChandRaatState.value) return "Itikaf ka waqt mukammal ho chuka hai. Aaj chand raat hai aur kal 21 March 2026 ko Eid hai.";
  if (isEidState.value) return "Itikaf kal chand raat par mukammal hua tha. Aaj Eid ul-Fitr ka din hai.";
  return "Itikaf Ramadan ke aakhri 10 din hota hai (20 Ramadan ki shaam se Eid ke chand tak). Maghrib ke baad Itikaf shuru hota hai.";
});

const heroCardLabel = computed(() =>
  isChandRaatState.value || isEidState.value ? "Itikaf Mukammal Hua" : "Itikaf Shuru Hoga",
);

const heroCardDate = computed(() => {
  if (isChandRaatState.value) return "20 March 2026";
  if (isEidState.value) return "21 March 2026";
  return "9 March 2026";
});

const heroCardSubtext = computed(() => {
  if (isChandRaatState.value) return "(Aaj raat chand raat hai)";
  if (isEidState.value) return "(Aaj Eid ul-Fitr hai)";
  return "(20 Ramadan ki shaam)";
});

const heroCardHint = computed(() => {
  if (isChandRaatState.value) return "Aaj raat Eid ki tayyari, fitrana aur dua ka waqt hai";
  if (isEidState.value) return "Aaj Eid namaz, takbeerat aur khushi ka din hai";
  return "Maghrib ke baad masjid mein dakhil ho jayein";
});

const endSectionLabel = computed(() =>
  isChandRaatState.value || isEidState.value ? "Ab kya hai:" : "Khatam hoga:",
);

const endCardTitle = computed(() => {
  if (isChandRaatState.value) return "Kal Eid ul-Fitr";
  if (isEidState.value) return "Eid Mubarak";
  return "Eid ka chand";
});

const endCardSubtitle = computed(() => {
  if (isChandRaatState.value) return "(21 March 2026)";
  if (isEidState.value) return "(Itikaf chand raat par mukammal hua)";
  return "(20 ya 21 March 2026)";
});

const statusNotice = computed(() => {
  if (isChandRaatState.value) return "Aaj chand raat hai. Fitrana aur kal ki Eid namaz ki tayyari mukammal kar lein.";
  if (isEidState.value) return "Aaj Eid ka din hai. Itikaf ka amal mukammal ho chuka hai.";
  return "Masjid mein 9 March 2026 ko Maghrib se pehle pohanch jayein.";
});

const detailParagraphOne = computed(() => {
  if (isChandRaatState.value) return "Itikaf 2026 Pakistan mein 9 March 2026 ko shuru hua tha aur aaj chand raat par apne ikhtitam ko pohanch chuka hai. Mo'takif logon ne Ramadan ke aakhri ashre mein ibadat, Quran aur dua mein waqt guzara. Itikaf ka asal maqsad Lailatul Qadr ki talash aur Allah se qurb hasil karna hota hai.";
  if (isEidState.value) return "Itikaf 2026 Pakistan mein chand raat tak jari raha aur ab mukammal ho chuka hai. Aaj Eid ul-Fitr ke din us ibadat ka samrah khushi, shukr aur dua ki surat mein samne aata hai. Itikaf ka asal maqsad Lailatul Qadr ki talash aur Allah se qurb hasil karna hota hai.";
  return "Itikaf 2026 Pakistan mein 9 March 2026 (20 Ramadan 1447 AH ki shaam) Maghrib ke baad shuru hoga. Is liye mo'takif log Maghrib se pehle masjid mein dakhil ho jate hain. Yeh Ramadan ke aakhri 10 din ka ibadat ka waqt hota hai jo Eid ka chand nazar aane tak jari rehta hai.";
});

const detailParagraphTwo = computed(() => {
  if (isChandRaatState.value) return "Pakistan mein Itikaf 20 Ramadan ki shaam se shuru hota hai aur chand raat par mukammal hota hai. Aaj raat ka markazi pegham yeh hai ke Itikaf ke baad bhi zikr, dua aur shukr ko jari rakha jaye.";
  if (isEidState.value) return "Pakistan mein Itikaf 20 Ramadan ki shaam se shuru hota hai aur Eid se pehle chand raat tak rehta hai. Aaj Eid ke din Itikaf ka paigham yeh hai ke Ramadan ki roohaniyat ko zindagi mein aagay bhi barqarar rakha jaye.";
  return "Pakistan mein har saal Itikaf 20 Ramadan ki shaam se shuru hota hai aur 29 ya 30 Ramadan tak rehta hai, jo chand dekhne par munhasir hota hai. Ramadan ki mukammal tareekhen dekhne ke liye aap Ramadan Calendar 2026 bhi check kar sakte hain.";
});

const countdownLabel = computed(() => {
  if (isChandRaatState.value) return "Aaj chand raat hai, kal Eid hai";
  if (isEidState.value) return "Aaj Eid ul-Fitr hai";

  const now = new Date();
  const target = new Date("2026-03-09T18:00:00+05:00");
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 1) return `${diffDays} din baqi`;
  if (diffDays === 1) return "1 din baqi";
  if (diffDays === 0) return "Aaj raat";
  return "Waqt guzar chuka";
});

useHead(() => ({
  title: "Itikaf 2026 Pakistan – 20 Ramadan (9 March) Kab Hai?",
  meta: [
    { name: "description", content: page.value?.meta?.description || "" },
    { name: "keywords", content: page.value?.meta?.keywords || "" },
    {
      property: "og:title",
      content: "Itikaf 2026 Pakistan – 20 Ramadan (9 March) Kab Hai?",
    },
    {
      property: "og:description",
      content: page.value?.meta?.description || "",
    },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://rozay.today/itikaf/" },
    { name: "twitter:card", content: "summary_large_image" },
    {
      name: "twitter:title",
      content: "Itikaf 2026 Pakistan – 20 Ramadan (9 March) Kab Hai?",
    },
    {
      name: "twitter:description",
      content: page.value?.meta?.description || "",
    },
  ],
  link: [{ rel: "canonical", href: "https://rozay.today/itikaf/" }],
  script: [
    {
      key: "itikaf-2026-faq",
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Itikaf 2026 kab shuru hoga?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Itikaf 2026 Ramzan ke 20vi tareekh ki shaam (Maghrib ke baad) shuru hoga. Pakistan mein umeedi tareekh 9 March 2026 (20 Ramadan 1447 AH) ki shaam hai. Itikaf Eid ka chand nazar aane tak rahega.",
            },
          },
          {
            "@type": "Question",
            name: "Aurton ke liye Itikaf ka kya hukm hai?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Auratain bhi Itikaf kar sakti hain. Unke liye masjid mein alahida jaga ya ghar mein ibadat ki jaga muqarrar ki jati hai. Shauhar ya wali ki ijazat lazmi hai. Pakistan mein hazaron auratain har saal ghar mein ya masjidon ke khas hisson mein Itikaf karti hain.",
            },
          },
        ],
      }),
    },
  ],
}));
</script>

